/**
 * TpinBranchPage - S-BRANCH, the branch drill-down (UX-RW-03 v1.1 S-BRANCH;
 * RW-01 S1 terminus, S7/S8; TL ruling Q1 option a).
 *
 * The branch's counts and freshness are its row in the TPIN's `/branches` list
 * (the per-branch summary is retired from the new SPA path, ADR-005 v1.6 3.4).
 * When the overview already loaded the list for this scope its row is used as
 * is; on a cold deep link the list is walked page by page until the row is
 * found. The walk is driven by the lookup state, one page per answer - no timer.
 * The lookup itself is shared with the S3 and S4 worklists (useBranchContext).
 *
 * Refresh is the only re-read (PD-33): it re-queries this TPIN's branch list
 * only. A period change is a new scope and starts the lookup again. An invalid
 * custom range is rejected inline before any query (FR-5).
 */

import { AppHeader } from '@components/AppHeader'
import { BranchHero } from '@components/BranchHero'
import { BranchOutcomes } from '@components/BranchOutcomes'
import { Breadcrumbs } from '@components/Breadcrumbs'
import { Link } from '@components/Link'
import { ErrorState, RefreshingFrame } from '@components/RealStates'
import { routePeriodUsable, useRoute } from '@components/route'
import { useBranchContext } from '@components/useBranchContext'
import { branchFreshness } from '@domain/tpinBranch'
import { tpinPath } from '@domain/tpinPaths'

export function TpinBranchPage({ tin, dbName }: { readonly tin: string; readonly dbName: string }) {
  const route = useRoute()
  const period = route.period
  const verdict = routePeriodUsable(period)
  // An unusable range is the empty string: no real date is empty, so one test says it all.
  const from = verdict.usable ? verdict.resolved.from : ''
  const to = verdict.usable ? verdict.resolved.to : from

  const { lookup, refreshing, refresh, retry, loadMore } = useBranchContext(tin, dbName, from, to)

  return (
    <div className="page page-branch" data-testid="branch-page">
      <AppHeader period={period} />
      <main className="page-main dash-main">
        <Breadcrumbs crumbs={[{ label: `TPIN ${tin}`, to: tpinPath(tin) }, { label: `Branch ${dbName}` }]} period={period} />
        <BranchHero
          tin={tin}
          dbName={dbName}
          period={period}
          freshness={branchFreshness(lookup)}
          refreshing={refreshing}
          onRefresh={refresh}
        />
        <h1 className="page-title branch-title" data-testid="branch-title">{`Branch ${dbName}`}</h1>
        <p className="dash-back">
          <Link to={tpinPath(tin)} period={period} className="dash-hero-change" testid="branch-back">
            Back to TPIN overview
          </Link>
        </p>
        {!verdict.usable ? (
          <ErrorState message={verdict.reason} />
        ) : (
          <RefreshingFrame refreshing={refreshing}>
            <BranchOutcomes
              lookup={lookup}
              tin={tin}
              dbName={dbName}
              from={from}
              to={to}
              period={period}
              onRetry={retry}
              onRetryWalk={loadMore}
            />
          </RefreshingFrame>
        )}
      </main>
    </div>
  )
}
